import React, { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../../context/AuthContext';

const ChatPage = () => {
  const { user } = useAuthContext();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize with welcome message and sample data
    const initialMessages = [
      {
        id: 1,
        sender: 'JetScanner360 Support',
        message: 'Welcome to JetScanner360 Live Chat! How can we assist you today?',
        timestamp: new Date(Date.now() - 300000).toISOString(),
        type: 'support'
      },
      {
        id: 2,
        sender: user?.name || 'Guest User',
        message: 'Hi, I need help with my booking.',
        timestamp: new Date(Date.now() - 240000).toISOString(),
        type: 'user'
      },
      {
        id: 3,
        sender: 'JetScanner360 Support',
        message: 'I\'d be happy to help you with your booking. Could you please provide your booking reference number?',
        timestamp: new Date(Date.now() - 180000).toISOString(),
        type: 'support'
      }
    ];

    setMessages(initialMessages);
    setOnlineUsers([
      { id: 1, name: 'Support Agent Sarah', status: 'online', avatar: 'S' },
      { id: 2, name: 'Concierge Team', status: 'online', avatar: 'C' },
      { id: 3, name: 'Flight Operations', status: 'away', avatar: 'F' }
    ]);
  }, [user]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    const message = {
      id: messages.length + 1,
      sender: user?.name || 'Guest User',
      message: newMessage,
      timestamp: new Date().toISOString(),
      type: 'user'
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');
    setIsTyping(true);

    // Simulate support response
    setTimeout(() => {
      const supportResponse = {
        id: messages.length + 2,
        sender: 'JetScanner360 Support',
        message: getAutoResponse(newMessage),
        timestamp: new Date().toISOString(),
        type: 'support'
      };
      setMessages(prev => [...prev, supportResponse]);
      setIsTyping(false);
    }, 2000);
  };

  const getAutoResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    
    if (message.includes('booking') || message.includes('reservation')) {
      return 'I can help you with your booking. Please provide your booking reference number, and I\'ll look up your reservation details.';
    } else if (message.includes('flight') || message.includes('schedule')) {
      return 'For flight information, you can check our Flight Status page or I can help you here. What specific flight information do you need?';
    } else if (message.includes('cancel') || message.includes('refund')) {
      return 'I understand you\'re inquiring about cancellation or refunds. Our cancellation policy varies by booking type. Let me connect you with our booking specialist who can review your specific case.';
    } else if (message.includes('payment') || message.includes('card')) {
      return 'For payment-related inquiries, I can help you update payment methods or resolve billing issues. What specific payment assistance do you need?';
    } else if (message.includes('aircraft') || message.includes('jet')) {
      return 'I can provide information about our aircraft fleet and availability. What type of aircraft or route are you interested in?';
    } else {
      return 'Thank you for your message. A support specialist will be with you shortly to provide personalized assistance. Is there anything specific I can help you with in the meantime?';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const quickReplies = [
    'Check my booking status',
    'Modify my reservation',
    'Flight information',
    'Payment assistance',
    'Cancellation policy',
    'Aircraft availability'
  ];

  return (
    <div className="min-h-screen bg-jet-gray-100 pt-20 pb-10">
      <div className="w-[86%] mx-auto">
        <div className="bg-white rounded-lg shadow-jet overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-black">Live Chat Support</h1>
                <p className="text-black opacity-80">Get instant help from our support team</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-black font-medium">Online</span>
              </div>
            </div>
          </div>

          <div className="flex h-[600px]">
            {/* Sidebar */}
            <div className="w-1/4 bg-jet-gray-50 border-r border-jet-gray-200 p-4">
              <h3 className="font-semibold text-jet-gray-800 mb-4">Support Team</h3>
              <div className="space-y-3">
                {onlineUsers.map(user => (
                  <div key={user.id} className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center font-semibold text-black">
                        {user.avatar}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                        user.status === 'online' ? 'bg-green-500' : 'bg-yellow-500'
                      }`}></div>
                    </div>
                    <div>
                      <p className="font-medium text-jet-gray-800 text-sm">{user.name}</p>
                      <p className="text-xs text-jet-gray-500 capitalize">{user.status}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <h3 className="font-semibold text-jet-gray-800 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button className="w-full text-left px-3 py-2 text-sm text-jet-gray-600 hover:bg-jet-gray-100 rounded">
                    View My Bookings
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-jet-gray-600 hover:bg-jet-gray-100 rounded">
                    Flight Status
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-jet-gray-600 hover:bg-jet-gray-100 rounded">
                    Contact Information
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-jet-gray-600 hover:bg-jet-gray-100 rounded">
                    FAQ
                  </button>
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.type === 'user'
                        ? 'bg-yellow-400 text-black'
                        : 'bg-jet-gray-100 text-jet-gray-800'
                    }`}>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-sm">{message.sender}</span>
                        <span className="text-xs opacity-70">{formatTime(message.timestamp)}</span>
                      </div>
                      <p className="text-sm">{message.message}</p>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-jet-gray-100 text-jet-gray-800 px-4 py-2 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-sm">JetScanner360 Support</span>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-jet-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-jet-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-jet-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Replies */}
              <div className="px-4 py-2 border-t border-jet-gray-200">
                <div className="flex flex-wrap gap-2">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => setNewMessage(reply)}
                      className="px-3 py-1 text-xs bg-jet-gray-100 hover:bg-jet-gray-200 text-jet-gray-700 rounded-full transition-colors duration-200"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-jet-gray-200">
                <form onSubmit={handleSendMessage} className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 border border-jet-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="bg-yellow-400 hover:bg-yellow-500 disabled:bg-jet-gray-300 text-black font-semibold px-6 py-2 rounded-lg transition-colors duration-200"
                  >
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-jet-gray-50 px-6 py-3 border-t border-jet-gray-200">
            <div className="flex items-center justify-between text-sm text-jet-gray-600">
              <div className="flex items-center space-x-4">
                <span>🔒 Secure Chat</span>
                <span>📞 Call: +1 (555) 123-4567</span>
                <span>✉️ Email: support@jetscanner360.com</span>
              </div>
              <div>
                Average response time: &lt; 2 minutes
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-white rounded-lg shadow-jet p-6">
          <h2 className="text-xl font-semibold text-jet-gray-800 mb-4">Need Immediate Assistance?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-jet-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-exclamation text-white text-xl"></i>
              </div>
              <h3 className="font-semibold text-jet-gray-800 mb-2">Emergency</h3>
              <p className="text-sm text-jet-gray-600 mb-3">24/7 emergency flight support</p>
              <p className="font-semibold text-red-600">+1 (555) 911-JETS</p>
            </div>
            <div className="text-center p-4 bg-jet-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-phone text-white text-xl"></i>
              </div>
              <h3 className="font-semibold text-jet-gray-800 mb-2">Phone Support</h3>
              <p className="text-sm text-jet-gray-600 mb-3">Speak with our team directly</p>
              <p className="font-semibold text-blue-600">+1 (555) 123-4567</p>
            </div>
            <div className="text-center p-4 bg-jet-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-envelope text-white text-xl"></i>
              </div>
              <h3 className="font-semibold text-jet-gray-800 mb-2">Email Support</h3>
              <p className="text-sm text-jet-gray-600 mb-3">Get detailed assistance via email</p>
              <p className="font-semibold text-green-600">support@jetscanner360.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;